import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { incomeService } from "@/services/incomeService";
import type {
  CommissionDistributionFilterParams,
  IncomeReportFilterParams,
} from "@/types/income.dto";
import { queryKeys } from "./keys";

export function useIncomeSummary() {
  return useQuery({
    queryKey: queryKeys.income.summary,
    queryFn: () => incomeService.getSummary(),
  });
}

export function useIncomeEntries(filter: IncomeReportFilterParams = {}) {
  return useQuery({
    queryKey: queryKeys.income.entries(filter),
    queryFn: () => incomeService.getEntries(filter),
  });
}

export function useAdminIncomeSummary() {
  return useQuery({
    queryKey: queryKeys.income.adminSummary,
    queryFn: () => incomeService.getAdminSummary(),
  });
}

export function useAdminIncomeEntries(filter: IncomeReportFilterParams = {}) {
  return useQuery({
    queryKey: queryKeys.income.adminEntries(filter),
    queryFn: () => incomeService.getAdminEntries(filter),
  });
}

export function useAdminCommissionDistribution(filter: CommissionDistributionFilterParams = {}) {
  return useQuery({
    queryKey: queryKeys.income.adminDistribution(filter as Record<string, unknown>),
    queryFn: () => incomeService.getAdminDistribution(filter),
    refetchInterval: 30_000,
  });
}

export function useRunDueCommissionDistributionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => incomeService.runDueDistribution(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["income", "admin-distribution"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.income.adminSummary });
    },
  });
}

export function useDistributeCommissionOrderMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, force }: { orderId: number; force?: boolean }) =>
      incomeService.distributeOrder(orderId, force ?? false),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["income", "admin-distribution"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.income.adminSummary });
    },
  });
}
